import { useEffect, useId, useRef, type KeyboardEvent } from "react";
import {
  Apple,
  Backpack,
  Bell,
  BookOpen,
  Check,
  Circle,
  CircleDot,
  Clock3,
  Cloud,
  Eraser,
  ListChecks,
  Moon,
  Package,
  Pencil,
  Ruler,
  Shirt,
  Sprout,
  Square,
  Star,
  Sun,
  Triangle,
  Umbrella,
  type LucideIcon
} from "lucide-react";

import type { ActivityHostRenderContext } from "@/features/activity-host";
import {
  useCurriculumFeedbackAudio,
  useCurriculumVoiceSequence,
  type CurriculumAudioPlayer
} from "@/features/audio";
import { CurriculumVoiceButton } from "@/ui";

import { UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER } from "./focus-social-audio";
import type {
  FocusSocialFollowDirectionsAction,
  FocusSocialFollowDirectionsRenderModel
} from "./follow-directions-adapter";
import "./focus-social-player.css";

export type FocusSocialFollowDirectionsRendererProps =
  ActivityHostRenderContext<
    FocusSocialFollowDirectionsAction,
    FocusSocialFollowDirectionsRenderModel
  > & {
    readonly audioPlayer?: CurriculumAudioPlayer;
  };

type DirectionVisualColor =
  "blue" | "coral" | "green" | "ink" | "violet" | "yellow";

function getDirectionTargetIcon(targetId: string): LucideIcon {
  if (targetId.includes("circle")) {
    return Circle;
  }
  if (targetId.includes("triangle")) {
    return Triangle;
  }
  if (targetId.includes("square")) {
    return Square;
  }
  if (targetId.includes("star")) {
    return Star;
  }

  switch (targetId) {
    case "apple":
      return Apple;
    case "backpack":
    case "backpack-on-chair":
      return Backpack;
    case "bell":
      return Bell;
    case "book-on-desk":
    case "picture-book":
      return BookOpen;
    case "cloud":
      return Cloud;
    case "clock-on-wall":
      return Clock3;
    case "eraser":
      return Eraser;
    case "lunch-box":
    case "lunch-box-on-chair":
      return Package;
    case "moon":
      return Moon;
    case "pencil":
      return Pencil;
    case "plant-by-window":
      return Sprout;
    case "ruler":
      return Ruler;
    case "coat-by-window":
      return Shirt;
    case "sun":
      return Sun;
    case "umbrella-by-door":
      return Umbrella;
    default:
      return CircleDot;
  }
}

function getDirectionVisualColor(targetId: string): DirectionVisualColor {
  if (targetId.includes("red") || targetId === "apple" || targetId === "bell") {
    return "coral";
  }
  if (
    targetId.includes("blue") ||
    targetId === "cloud" ||
    targetId.includes("book")
  ) {
    return "blue";
  }
  if (targetId.includes("green") || targetId.includes("plant")) {
    return "green";
  }
  if (targetId === "moon" || targetId.includes("coat")) {
    return "violet";
  }
  if (
    targetId.includes("yellow") ||
    targetId === "sun" ||
    targetId.includes("pencil") ||
    targetId.includes("ruler")
  ) {
    return "yellow";
  }
  return "ink";
}

function getDirectionVisualScale(
  targetId: string
): "large" | "regular" | "small" {
  if (targetId.startsWith("large-")) {
    return "large";
  }
  if (targetId.startsWith("small-")) {
    return "small";
  }
  return "regular";
}

export function FocusSocialFollowDirectionsRenderer({
  audioPlayer = UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER,
  getActivationProps,
  model,
  reducedMotion
}: FocusSocialFollowDirectionsRendererProps) {
  const titleId = useId();
  const targetRefs = useRef(new Map<string, HTMLButtonElement>());
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousCompletedStepCount = useRef(model.completedStepCount);
  const shouldFocusNextTarget = useRef(false);
  const stepPlayback =
    model.currentStepAudioPath === null ||
    model.currentStepPlaybackRequest === null
      ? null
      : {
          audioPath: model.currentStepAudioPath,
          playbackRequest: model.currentStepPlaybackRequest
        };
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback,
    false
  );
  const stepAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    stepPlayback,
    false
  );
  useCurriculumVoiceSequence(
    audioPlayer,
    [
      model.feedback,
      model.feedback === null || model.feedback.kind === "progress"
        ? stepPlayback
        : null
    ],
    model.status !== "completed"
  );

  useEffect(() => {
    const completedStepChanged =
      previousCompletedStepCount.current !== model.completedStepCount;
    previousCompletedStepCount.current = model.completedStepCount;

    if (!shouldFocusNextTarget.current) {
      return;
    }

    shouldFocusNextTarget.current = false;

    if (!completedStepChanged || model.status === "completed") {
      return;
    }

    const nextTarget = model.targets.find(({ isAvailable }) => isAvailable);

    if (nextTarget !== undefined) {
      targetRefs.current.get(nextTarget.id)?.focus();
    }
  }, [model]);

  useEffect(() => {
    if (model.status === "completed") {
      resultHeadingRef.current?.focus();
    }
  }, [model.status]);

  function focusAdjacentTarget(
    event: KeyboardEvent<HTMLButtonElement>,
    targetId: string,
    offset: -1 | 1
  ) {
    const availableIds = model.targets
      .filter(({ isAvailable }) => isAvailable)
      .map(({ id }) => id);
    const currentIndex = availableIds.indexOf(targetId);

    if (availableIds.length === 0 || currentIndex === -1) {
      return;
    }

    event.preventDefault();

    const nextIndex =
      (currentIndex + offset + availableIds.length) % availableIds.length;
    const nextId = availableIds[nextIndex];

    if (nextId !== undefined) {
      targetRefs.current.get(nextId)?.focus();
    }
  }

  function handleTargetKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    targetId: string
  ) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        focusAdjacentTarget(event, targetId, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        focusAdjacentTarget(event, targetId, -1);
        break;
    }
  }

  if (model.status === "completed") {
    return (
      <section
        aria-labelledby={titleId}
        className="focus-social-player focus-social-directions focus-social-player--complete"
        data-reduced-motion={reducedMotion}
      >
        <Check
          aria-hidden="true"
          className="focus-social-player__complete-icon"
        />
        <h2 id={titleId} ref={resultHeadingRef} tabIndex={-1}>
          每一步都接上啦
        </h2>
        <p>你照着顺序，把它们一个个完成了。</p>
      </section>
    );
  }

  const visibleTargets = model.targets.filter(
    (target) => target.isAvailable || target.isCompleted
  );

  return (
    <section
      aria-labelledby={titleId}
      className="focus-social-player focus-social-directions"
      data-reduced-motion={reducedMotion}
    >
      <header className="focus-social-player__activity-header">
        <div>
          <p className="focus-social-player__eyebrow">
            第 {model.currentStepNumber} / {model.totalStepCount} 步
          </p>
          <div className="focus-social-player__prompt-title-row">
            <h2 id={titleId}>
              {model.currentStepCaption ?? "按顺序完成每一步。"}
            </h2>
            {model.currentStepAudioPath === null ? null : (
              <CurriculumVoiceButton
                isPlaying={stepAudio.isPlaying}
                label={model.currentStepCaption ?? "当前步骤"}
                onPlay={stepAudio.replay}
              />
            )}
          </div>
        </div>
        <div
          aria-label={
            "已经完成 " +
            model.completedStepCount +
            " 步，共 " +
            model.totalStepCount +
            " 步"
          }
          className="focus-social-player__progress"
          role="status"
        >
          <ListChecks aria-hidden="true" />
          <span>
            {model.completedStepCount} / {model.totalStepCount}
          </span>
        </div>
      </header>

      <div className="focus-social-player__feedback-row">
        <p
          aria-live="polite"
          className="focus-social-player__feedback"
          data-feedback-kind={model.feedback?.kind ?? "none"}
          role="status"
        >
          {model.feedback?.message ?? "先听听点点的顺序，再慢慢选。"}
        </p>
        {model.feedback === null ? null : (
          <CurriculumVoiceButton
            isPlaying={feedbackAudio.isPlaying}
            label={model.feedback.message}
            onPlay={feedbackAudio.replay}
          />
        )}
      </div>

      <div
        aria-label="指令中的物品"
        className="focus-social-player__choice-grid"
        role="group"
      >
        {visibleTargets.map((target) => {
          const activationProps = getActivationProps((inputMethod) => ({
            type: "perform-step",
            action: "select",
            targetId: target.id,
            inputMethod
          }));
          const TargetIcon = getDirectionTargetIcon(target.id);

          return (
            <button
              {...activationProps}
              aria-label={
                target.isCompleted
                  ? "已完成" + target.label
                  : "选择" + target.label
              }
              aria-pressed={target.isCompleted}
              className="focus-social-player__choice"
              data-completed={target.isCompleted}
              data-hinted={target.isHinted}
              disabled={target.isCompleted}
              key={target.id}
              onClickCapture={(event) => {
                shouldFocusNextTarget.current = event.detail === 0;
              }}
              onKeyDown={(event) => handleTargetKeyDown(event, target.id)}
              ref={(node) => {
                if (node === null) {
                  targetRefs.current.delete(target.id);
                } else {
                  targetRefs.current.set(target.id, node);
                }
              }}
              type="button"
            >
              <span
                aria-hidden="true"
                className="focus-social-player__choice-mark"
                data-target-visual={target.id}
                data-visual-color={getDirectionVisualColor(target.id)}
                data-visual-scale={getDirectionVisualScale(target.id)}
              >
                {target.isCompleted ? <Check /> : <TargetIcon />}
              </span>
              <span className="focus-social-player__choice-label">
                {target.label}
              </span>
              {target.isHinted ? (
                <strong className="focus-social-player__choice-status">
                  这一步
                </strong>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
